/**
 * AlvearDashboard.jsx  v5
 * Agrega TankHistoryModal y StripHistoryModal al hacer click
 */

import { useState, useEffect } from 'react';
import {
    Badge, Box, Chip, Divider, Grid, IconButton, Paper,
    Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup,
    Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
    BarChart as BarChartIcon,
    Dashboard as DashboardIcon,
    Notifications as NotificationsIcon,
    Settings as SettingsIcon,
    Wifi as WifiIcon,
    WifiOff as WifiOffIcon,
    Water as WaterIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useSnackbar } from 'notistack';

import TankCard from './Tankcard';
import HistoryPanel from './Historypanel';
import AlarmDrawer from './Alarmdrawer';
import ConfigDialog from './Configdialog';
import TankHistoryModal from './Tankhistorymodal';
import StripHistoryModal from './Striphistorymodal';
import {
    TANKS, buildHistory, checkAlarms, defaultConfigs,
    makeSample, calcMasa, calcDensidad,
} from './Hipergas.utils';
import Navbar from '../../../../Navbar';

export default function AlvearDashboard({
    apiUrl = 'http://localhost:3001',
    socketUrl = '',
    simulate = false,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();

    const [history, setHistory] = useState(() => buildHistory());
    const [current, setCurrent] = useState(() => buildHistory().at(-1));
    const [configs, setConfigs] = useState(() => defaultConfigs());
    const [alarms, setAlarms] = useState([{
        id: 'boot', hora: new Date().toLocaleTimeString('es-AR'),
        tank: 'Sistema', msg: 'Monitor inicializado · servicio_italgas.alvear', sev: 'info',
    }]);
    const [view, setView] = useState('realtime');
    const [chartVar, setChartVar] = useState('nivel');
    const [selectedTank, setSelectedTank] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [showAlarms, setShowAlarms] = useState(false);
    const [connected, setConnected] = useState(simulate);
    const [lastUpdate, setLastUpdate] = useState(new Date());

    // Modales de historial
    const [tankModal, setTankModal] = useState({ open: false, tank: null });
    const [stripModal, setStripModal] = useState({ open: false, tipo: null });

    // ── Cargar config desde BD ─────────────────────────────────────────────────
    useEffect(() => {
        fetch(`${apiUrl}/api/config/alvear`, {
            headers: { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' }
        })
            .then((r) => r.ok ? r.json() : null)
            .then((rows) => {
                if (!rows) return;
                const map = {};
                rows.forEach((r) => { map[r.tanque_id] = r; });
                setConfigs(map);
            })
            .catch(() => { });
    }, [apiUrl]);

    // ── Simulación ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!simulate) return;
        const iv = setInterval(() => {
            setCurrent((prev) => {
                const next = makeSample(prev);
                checkAlarms(next, prev, configs, enqueueSnackbar, setAlarms);
                setHistory((h) => [...h.slice(-79), next]);
                setLastUpdate(new Date());
                return next;
            });
        }, 2000);
        return () => clearInterval(iv);
    }, [simulate, configs]);

    // ── Backend real ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (simulate) return;
        const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };

        const fetchLatest = () =>
            fetch(`${apiUrl}/api/alvear/latest`, { headers })
                .then((r) => r.json())
                .then((d) => { setCurrent(d); setConnected(true); setLastUpdate(new Date()); })
                .catch(() => setConnected(false));

        fetchLatest();

        fetch(`${apiUrl}/api/alvear/history?limit=40`, { headers })
            .then((r) => r.json())
            .then((d) => setHistory(d))
            .catch(() => { });

        // Polling cada 30s como respaldo al WebSocket
        const poll = setInterval(() => {
            fetch(`${apiUrl}/api/alvear/latest`, { headers })
                .then((r) => r.json())
                .then((d) => {
                    setCurrent((prev) => {
                        if (d.id === prev?.id) return prev;
                        checkAlarms(d, prev, configs, enqueueSnackbar, setAlarms);
                        setHistory((h) => [...h.slice(-79), d]);
                        setLastUpdate(new Date());
                        return d;
                    });
                    setConnected(true);
                })
                .catch(() => setConnected(false));
        }, 30000);

        if (!socketUrl) return () => clearInterval(poll);

        import('socket.io-client').then(({ io }) => {
            const socket = io(socketUrl, { transports: ['websocket'] });
            socket.on('connect', () => setConnected(true));
            socket.on('disconnect', () => setConnected(false));
            socket.on('alvear:update', (d) => {
                setCurrent((prev) => {
                    checkAlarms(d, prev, configs, enqueueSnackbar, setAlarms);
                    setHistory((h) => [...h.slice(-79), d]);
                    setLastUpdate(new Date());
                    return d;
                });
            });
            return () => { socket.disconnect(); clearInterval(poll); };
        });

        return () => clearInterval(poll);
    }, [simulate, apiUrl, socketUrl]);

    // ── Stats strip ────────────────────────────────────────────────────────────
    const avgNivel = (TANKS.reduce((a, t) => a + (current[`TK_${t.id}`] || 0), 0) / TANKS.length).toFixed(0);

    const tnPorProducto = TANKS.reduce((acc, t) => {
        const cfg = configs[t.id] || {};
        const nivel = current[`TK_${t.id}`] || 0;
        const temp = current[`TEMP_${t.id}`] || 15;
        const masa = calcMasa(cfg, nivel, temp);
        const tn = masa.unidad === 'tn' ? parseFloat(masa.valor) : 0;
        const prod = cfg.producto || 'propano';
        acc[prod] = (acc[prod] || 0) + tn;
        return acc;
    }, {});

    const tnPropano = (tnPorProducto.propano || 0).toFixed(1);
    const tnButano = (tnPorProducto.butano || 0).toFixed(1);
    const tnTotal = Object.values(tnPorProducto).reduce((a, b) => a + b, 0).toFixed(1);

    const dangerCount = alarms.filter((a) => a.sev === 'danger').length;
    const warnCount = alarms.filter((a) => a.sev === 'warn').length;
    const alarmTotal = dangerCount + warnCount;

    function handleConfigSave(updatedMap) {
        setConfigs(updatedMap);
        enqueueSnackbar('Configuración actualizada', { variant: 'success', autoHideDuration: 3000 });
    }

    // ── Strip boxes — clickeables ──────────────────────────────────────────────
    const stripBoxes = [
        { label: 'Nivel promedio', val: `${avgNivel}%`, sub: 'todos los tanques', color: 'info.main', tipo: 'nivel' },
        { label: 'Propano (tn)', val: tnPropano, sub: TANKS.filter((t) => (configs[t.id]?.producto || 'propano') === 'propano').map((t) => t.label).join(' · ') || '—', color: '#38BDF8', tipo: 'propano' },
        { label: 'Butano (tn)', val: tnButano, sub: TANKS.filter((t) => configs[t.id]?.producto === 'butano').map((t) => t.label).join(' · ') || '—', color: '#FB923C', tipo: 'butano' },
        { label: 'Total disponible (tn)', val: tnTotal, sub: 'propano + butano', color: 'success.main', tipo: 'total' },
        {
            label: 'Alarmas activas', val: alarmTotal, sub: alarmTotal === 0 ? 'sin alarmas' : `${dangerCount} críticas · ${warnCount} avisos`,
            color: dangerCount > 0 ? 'error.main' : warnCount > 0 ? 'warning.main' : 'success.main', tipo: null
        },
    ];

    return (
        <>
            <Navbar />
            <Box sx={{ mt: '10px', mb: '60px', bgcolor: 'background.default', minHeight: '100%' }}>
                {/* HEADER */}
                <Paper elevation={0} sx={{ px: 2, py: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', borderRadius: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WaterIcon sx={{ color: '#fff', fontSize: 16 }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} fontSize={13} lineHeight={1.2}>Hipergas · Alvear</Typography>
                            <Typography variant="caption" color="text.disabled" fontSize={10} lineHeight={1}>servicio_italgas.alvear</Typography>
                        </Box>
                        {!isMobile && (
                            <>
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
                                    <ToggleButton value="realtime" sx={{ px: 1.5, gap: 0.5, fontSize: 11, py: 0.5 }}>
                                        <DashboardIcon sx={{ fontSize: 14 }} /> Tiempo real
                                    </ToggleButton>
                                    <ToggleButton value="history" sx={{ px: 1.5, gap: 0.5, fontSize: 11, py: 0.5 }}>
                                        <BarChartIcon sx={{ fontSize: 14 }} /> Histórico
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </>
                        )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip size="small"
                            icon={connected ? <WifiIcon sx={{ fontSize: '12px !important' }} /> : <WifiOffIcon sx={{ fontSize: '12px !important' }} />}
                            label={connected ? 'En línea' : 'Sin conexión'}
                            color={connected ? 'success' : 'error'} variant="outlined"
                            sx={{ fontSize: 10, height: 22 }} />
                        <IconButton size="small" onClick={() => setShowAlarms(true)}>
                            <Badge badgeContent={alarmTotal} color={dangerCount > 0 ? 'error' : 'warning'} max={99}>
                                <NotificationsIcon fontSize="small" color={alarmTotal > 0 ? (dangerCount > 0 ? 'error' : 'warning') : 'action'} />
                            </Badge>
                        </IconButton>
                        <IconButton size="small" onClick={() => setShowConfig(true)}>
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                        {!isMobile && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11 }}>
                                {lastUpdate.toLocaleTimeString('es-AR', { hour12: false })}
                            </Typography>
                        )}
                    </Stack>
                </Paper>

                {isMobile && (
                    <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={view} onChange={(_, v) => setView(v)} variant="fullWidth">
                            <Tab value="realtime" label="Tiempo real" sx={{ fontSize: 11 }} />
                            <Tab value="history" label="Histórico" sx={{ fontSize: 11 }} />
                        </Tabs>
                    </Paper>
                )}

                <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1200, mx: 'auto' }}>

                    {view === 'realtime' && (
                        <>
                            {/* STRIP — clickeable para abrir historial */}
                            <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
                                {stripBoxes.map((s, i) => (
                                    <Grid item xs={6} sm={4} md key={i} sx={{ display: 'flex' }}>
                                        <Paper
                                            variant="outlined"
                                            onClick={() => s.tipo && setStripModal({ open: true, tipo: s.tipo })}
                                            sx={{
                                                p: 1.5, pl: 2, width: '100%',
                                                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                position: 'relative',
                                                cursor: s.tipo ? 'pointer' : 'default',
                                                transition: 'all 0.15s',
                                                '&:hover': s.tipo ? { borderColor: s.color, transform: 'translateY(-1px)' } : {},
                                            }}
                                        >
                                            <Typography variant="caption" sx={{
                                                position: 'absolute', top: -9, left: 12,
                                                bgcolor: 'background.paper', px: 0.8,
                                                fontSize: 9, fontWeight: 600, color: 'text.disabled',
                                                textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap',
                                            }}>
                                                {s.label}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Orbitron', sans-serif", color: s.color, fontWeight: 700, fontSize: { xs: 18, sm: 22 }, lineHeight: 1.2 }}>
                                                {s.val}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9, mt: 0.2 }}>
                                                {s.sub}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* TANK CARDS — click abre modal de historial del tanque */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {TANKS.map((t) => (
                                    <Grid item xs={12} sm={6} key={t.id}>
                                        <TankCard
                                            tank={t}
                                            current={current}
                                            cfg={configs[t.id] || {}}
                                            selected={selectedTank}
                                            onSelect={(id) => {
                                                setSelectedTank((s) => (s === id ? null : id));
                                                setTankModal({ open: true, tank: t });
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            {/* MINI CHART NIVEL */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="caption" fontWeight={600} fontSize={12}>Nivel — histórico reciente</Typography>
                                    <Stack direction="row" spacing={1.5}>
                                        {TANKS.map((t) => (
                                            <Stack key={t.id} direction="row" alignItems="center" spacing={0.5}>
                                                <Box sx={{ width: 12, height: 2, bgcolor: t.color, borderRadius: 1 }} />
                                                <Typography variant="caption" color="text.disabled" fontSize={10}>T{t.id}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Stack>
                                <LineChart
                                    dataset={history.slice(-20)}
                                    series={TANKS.map((t) => ({ dataKey: `TK_${t.id}`, label: t.label, color: t.color, showMark: false, curve: 'monotoneX' }))}
                                    xAxis={[{ dataKey: 'fecha', scaleType: 'band', tickLabelStyle: { fontSize: 9 } }]}
                                    yAxis={[{ min: 0, max: 100, tickLabelStyle: { fontSize: 9 }, label: '%' }]}
                                    height={200}
                                    margin={{ left: 36, right: 12, top: 6, bottom: 22 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                            </Paper>
                        </>
                    )}

                    {view === 'history' && (
                        <HistoryPanel current={current} chartVar={chartVar} onChartVarChange={setChartVar} apiUrl={apiUrl} />
                    )}
                </Box>

                {/* MODALES */}
                <AlarmDrawer open={showAlarms} alarms={alarms} onClear={() => setAlarms([])} onClose={() => setShowAlarms(false)} />
                <ConfigDialog open={showConfig} apiUrl={apiUrl} tabla="alvear" onSave={handleConfigSave} onClose={() => setShowConfig(false)} />

                {/* Modal historial de tanque */}
                <TankHistoryModal
                    open={tankModal.open}
                    tank={tankModal.tank}
                    cfg={tankModal.tank ? configs[tankModal.tank.id] : null}
                    apiUrl={apiUrl}
                    onClose={() => setTankModal({ open: false, tank: null })}
                />

                {/* Modal historial del strip */}
                <StripHistoryModal
                    open={stripModal.open}
                    tipo={stripModal.tipo}
                    configs={configs}
                    apiUrl={apiUrl}
                    onClose={() => setStripModal({ open: false, tipo: null })}
                />
            </Box>
        </>
    );
}